import { type HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { extractErrorMessage } from '@core/utils/extract-error-message.util';
import { CustomToastService } from '../../shared/components/custom-toast/custom-toast.service';

export interface ErrorHandlingConfig {
  skipErrorHandlingHeader: string;
  excludedStatusCodes: number[];
}

export const ERROR_HANDLING_CONFIG: ErrorHandlingConfig = {
  skipErrorHandlingHeader: 'X-Skip-Error-Handling',
  excludedStatusCodes: [
    401, // Unauthorized - handled by auth interceptor
    403, // Forbidden - handled by auth interceptor
  ],
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const customToastService = inject(CustomToastService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if the error should be handled by the interceptor
      if (shouldHandleError(req, error)) {
        const errorMessage = extractErrorMessage(error);

        if (errorMessage) {
          customToastService.open({
            message: errorMessage,
            snackType: 'error',
          });
        }
      }

      return throwError(() => error);
    })
  );
};

// Check if an error should be handled by this interceptor
function shouldHandleError(req: any, error: HttpErrorResponse): boolean {
  // Check if request has the skip header
  if (req.headers.has(ERROR_HANDLING_CONFIG.skipErrorHandlingHeader)) {
    return false;
  }

  // Check excluded status codes
  if (ERROR_HANDLING_CONFIG.excludedStatusCodes.includes(error.status)) {
    return false;
  }

  // Only client and server errors (4xx, 5xx)
  return error.status >= 400;
}
